import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Wallet, Search, HandHeart, Check, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * CommonsStart — single-screen "Make this group last" flow.
 *
 * Self fund is now wired to Stripe Checkout (monthly/yearly — yearly default).
 * Find sponsor and Already have support remain as placeholder states per
 * the directive scope (not built yet).
 */
export default function CommonsStart() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const groupId = params.get('group_id');
  const [group, setGroup] = useState(null);
  const [selected, setSelected] = useState(null);
  const [cycle, setCycle] = useState('yearly');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!groupId) return;
    axios
      .get(`${API}/api/groups/${groupId}/profile`, { withCredentials: true })
      .then((r) => setGroup(r.data))
      .catch(() => { /* silent */ });
  }, [groupId]);

  const pick = (key) => {
    setSelected(key);
    if (key === 'sponsor') toast.success('Noted — we\'ll let you know when the sponsor directory opens.');
    if (key === 'existing') toast.success('Noted — we\'ll reach out about applying existing support.');
    // "self" reveals the inline monthly/yearly + Keep it going button (no toast).
  };

  const keepItGoing = async () => {
    if (submitting || !groupId) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/commons/self-fund/checkout`,
        {
          group_id: groupId,
          cycle,
          origin_url: window.location.origin,
        },
        { withCredentials: true },
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start payment. Try again.');
        setSubmitting(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not start payment. Try again.';
      toast.error(typeof msg === 'string' ? msg : 'Could not start payment. Try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }} data-testid="commons-start-page">
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-xl mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 rounded-lg hover:opacity-70"
              aria-label="Back"
              data-testid="commons-start-back"
            >
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <h1 className="text-lg font-bold truncate" style={{ color: 'var(--fy-text-primary)' }}>
              Make this group last
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6 space-y-6">
        {/* Context header */}
        <div className="space-y-2">
          {group?.name && (
            <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: 'var(--fy-text-disabled)' }}>
              {group.name}
            </p>
          )}
          <h2
            className="text-2xl font-extrabold leading-tight"
            style={{ color: 'var(--fy-text-primary)', fontFamily: 'Outfit, system-ui' }}
            data-testid="commons-start-headline"
          >
            Keep this group going.
          </h2>
          <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            Commons keeps the group steady over time. No boost, no ranking, no promotion.
          </p>
        </div>

        {/* Three options */}
        <div className="space-y-3">
          {/* SELF FUND — wired to Stripe */}
          <div
            className="w-full text-left p-4 rounded-2xl transition-all"
            style={{
              backgroundColor: 'var(--fy-surface-card)',
              border: selected === 'self' ? '1px solid #8B6A4F' : '1px solid var(--fy-border-subtle)',
              boxShadow: selected === 'self' ? '0 2px 10px rgba(139,106,79,0.12)' : '0 1px 4px rgba(0,0,0,0.03)',
            }}
            data-testid="commons-option-self"
          >
            <button
              type="button"
              onClick={() => pick('self')}
              className="w-full flex items-start gap-3 text-left"
            >
              <div
                className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: 'var(--fy-surface-muted)' }}
              >
                <Wallet className="w-5 h-5" style={{ color: '#4F6D4A' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold" style={{ color: 'var(--fy-text-primary)' }}>
                    Keep it going yourself
                  </p>
                  {selected === 'self' && (
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: '#4F6D4A' }} />
                  )}
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
                  Cover Commons yourself. You stay the organizer.
                </p>
              </div>
            </button>

            {selected === 'self' && (
              <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--fy-border-subtle)' }} data-testid="commons-self-panel">
                {/* Monthly / Yearly toggle (yearly pre-selected) */}
                <div className="grid grid-cols-2 gap-2">
                  <CycleButton
                    testid="commons-cycle-monthly"
                    active={cycle === 'monthly'}
                    onClick={() => setCycle('monthly')}
                    label="Monthly"
                    amount="$20"
                  />
                  <CycleButton
                    testid="commons-cycle-yearly"
                    active={cycle === 'yearly'}
                    onClick={() => setCycle('yearly')}
                    label="Yearly"
                    amount="$200"
                  />
                </div>

                <button
                  type="button"
                  onClick={keepItGoing}
                  disabled={submitting}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-bold transition-transform active:scale-[0.99] disabled:opacity-60"
                  style={{ backgroundColor: '#8B6A4F', color: '#FFFFFF' }}
                  data-testid="commons-keep-going"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Redirecting…
                    </>
                  ) : (
                    'Continue'
                  )}
                </button>
              </div>
            )}
          </div>

          {/* FIND SPONSOR — placeholder */}
          <OptionCard
            testid="commons-option-sponsor"
            active={selected === 'sponsor'}
            onClick={() => pick('sponsor')}
            icon={<Search className="w-5 h-5" style={{ color: '#A87A3D' }} />}
            title="Find sponsor"
            body="Ask a business to support this group. They get acknowledgment — no promotion, no ranking."
            placeholder={selected === 'sponsor'}
          />

          {/* ALREADY HAVE SUPPORT — placeholder */}
          <OptionCard
            testid="commons-option-existing"
            active={selected === 'existing'}
            onClick={() => pick('existing')}
            icon={<HandHeart className="w-5 h-5" style={{ color: '#8E4A58' }} />}
            title="Already have support"
            body="A donor or sponsor already covers this. Apply that support here."
            placeholder={selected === 'existing'}
          />
        </div>
      </main>
    </div>
  );
}

function CycleButton({ testid, active, onClick, label, amount }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-2.5 rounded-xl text-sm font-semibold transition-all"
      style={{
        backgroundColor: active ? '#8B6A4F' : 'var(--fy-surface-muted)',
        color: active ? '#FFFFFF' : 'var(--fy-text-primary)',
        border: active ? '1px solid #8B6A4F' : '1px solid var(--fy-border-subtle)',
      }}
      data-testid={testid}
    >
      <span>{label}</span>
      <span className="ml-2 opacity-80 font-normal">{amount}</span>
    </button>
  );
}

function OptionCard({ testid, active, onClick, icon, title, body, placeholder }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left p-4 rounded-2xl transition-all active:scale-[0.99] hover:shadow-sm"
      style={{
        backgroundColor: 'var(--fy-surface-card)',
        border: active ? '1px solid #8B6A4F' : '1px solid var(--fy-border-subtle)',
        boxShadow: active ? '0 2px 10px rgba(139,106,79,0.12)' : '0 1px 4px rgba(0,0,0,0.03)',
      }}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: 'var(--fy-surface-muted)' }}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold" style={{ color: 'var(--fy-text-primary)' }}>
              {title}
            </p>
            {active && (
              <Check className="w-4 h-4 flex-shrink-0" style={{ color: '#4F6D4A' }} />
            )}
          </div>
          <p className="text-xs mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
            {body}
          </p>
          {placeholder && (
            <div
              className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg"
              style={{ backgroundColor: 'var(--fy-surface-muted)' }}
              data-testid={`${testid}-placeholder`}
            >
              <Clock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
              <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                Coming soon. We'll reach out when this path is ready.
              </p>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
